import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, Image as ImageIcon, File, Loader2, Download, Trash2 } from 'lucide-react';
import { useSermonMedia } from '../hooks/useSermonMedia';

interface MediaUploaderProps {
  sermonId: string;
  sectionId: string;
  onClose: () => void;
}

interface MediaFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  publicUrl?: string;
}

export function MediaUploader({ sermonId, sectionId, onClose }: MediaUploaderProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { loadSectionMedia, uploadFile, deleteFile, getPublicUrl } = useSermonMedia(sermonId);

  // Load existing media for this section
  useEffect(() => {
    const loadMedia = async () => {
      const sectionMedia = await loadSectionMedia(sermonId, sectionId);
      const mediaWithUrls = sectionMedia.map(media => ({
        ...media,
        publicUrl: getPublicUrl(media.file_path)
      }));
      setFiles(mediaWithUrls);
    };
    
    loadMedia();
  }, [sermonId, sectionId, loadSectionMedia, getPublicUrl]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      for (const file of acceptedFiles) {
        const result = await uploadFile({
          sermonId,
          sectionId,
          file,
          onProgress: setUploadProgress
        });
        
        if (result) {
          setFiles(prev => [
            {
              id: result.id,
              file_name: result.file_name,
              file_type: result.file_type,
              file_size: result.file_size,
              file_path: result.file_path,
              publicUrl: result.publicUrl
            },
            ...prev
          ]);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  }, [sermonId, sectionId, uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: uploading
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const success = await deleteFile(id);
      if (success) {
        setFiles(prev => prev.filter(file => file.id !== id));
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-electric" />;
    if (fileType.includes('pdf')) return <FileText className="h-5 w-5 text-crimson" />;
    if (fileType.includes('word') || fileType.includes('doc')) return <FileText className="h-5 w-5 text-electric" />;
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return <FileText className="h-5 w-5 text-gold" />;
    return <File className="h-5 w-5 text-slate" />;
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-midnight">Media Files</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate hover:text-midnight rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-electric bg-electric/5' : 'border-sand hover:border-electric/50'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          
          {uploading ? (
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-electric mx-auto mb-2" />
              <p className="text-slate">Uploading... {Math.round(uploadProgress)}%</p>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-slate mx-auto mb-2" />
              <p className="text-slate">Drag & drop files here, or click to select files</p>
              <p className="text-xs text-slate mt-1">
                Supported formats: Images, PDF, Word, PowerPoint (Max 10MB)
              </p>
            </>
          )}
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-slate mb-2">Uploaded Files</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {files.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-2 bg-sand/10 rounded-lg">
                  <div className="flex items-center overflow-hidden">
                    {getFileIcon(file.file_type)}
                    <span className="ml-2 text-sm text-midnight truncate" title={file.file_name}>
                      {file.file_name}
                    </span>
                    <span className="ml-2 text-xs text-slate">
                      {formatFileSize(file.file_size)}
                    </span>
                  </div>
                  <div className="flex space-x-1 flex-shrink-0">
                    <a
                      href={file.publicUrl}
                      download={file.file_name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-slate hover:text-electric rounded"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => handleDelete(file.id)}
                      className="p-1 text-slate hover:text-crimson rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-sand/20 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate hover:text-midnight"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return modalContent;
}